import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Rotate3d, Image as ImageIcon } from "lucide-react";
import { useQuote } from "../context/QuoteContext";
import Product360 from "./Product360";

const ease = [0.16, 1, 0.3, 1];

export default function ProductDetail({ product, onClose }) {
  const { openQuote } = useQuote();
  const [view, setView] = useState("photo");
  useEffect(() => { setView("photo"); }, [product]);
  return (
    <AnimatePresence>
      {product && (
        <>
          <motion.div
            className="fixed inset-0 z-[90] bg-[#0A1128]/70 backdrop-blur-sm"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="fixed inset-0 z-[91] flex items-end sm:items-center justify-center p-0 sm:p-6 pointer-events-none"
          >
            <motion.div
              className="pointer-events-auto bg-white w-full sm:max-w-4xl max-h-[92vh] overflow-y-auto"
              initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
              transition={{ duration: 0.5, ease }}
              data-testid="product-detail"
            >
              <div className="grid grid-cols-1 md:grid-cols-2">
                <div className="relative bg-[#F1F5F9] flex items-center justify-center p-10 min-h-[300px] md:min-h-[440px]">
                  <button onClick={onClose} className="absolute top-5 left-5 md:hidden p-1 z-10" aria-label="Close">
                    <X size={24} className="text-[#0A1128]" />
                  </button>

                  {product.model3d && view === "3d" ? (
                    <div className="w-full h-[340px] md:h-full">
                      <Product360 kind={product.model3d} image={product.img} alt={product.name} />
                    </div>
                  ) : (
                    product.img ? (
                      <motion.img
                        key="photo"
                        src={product.img} alt={product.name} loading="lazy"
                        className="max-h-[340px] w-auto object-contain mix-blend-multiply"
                        initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.6, ease }}
                      />
                    ) : (
                      <div className="text-center select-none px-8">
                        <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#1F6FEB]">{product.categoryName}</p>
                        <p className="mt-4 text-5xl font-extrabold tracking-tighter text-[#0A1128]/[0.12]">{product.model || "Clinixport"}</p>
                      </div>
                    )
                  )}

                  {product.model3d ? (
                    <div className="absolute bottom-5 left-5 flex bg-white border border-[#0A1128]/10 p-0.5" data-testid="product-view-toggle">
                      <button
                        onClick={() => setView("photo")}
                        data-testid="view-photo"
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider transition-colors ${view === "photo" ? "bg-[#0A1128] text-white" : "text-[#0A1128] hover:bg-[#0A1128]/5"}`}
                      >
                        <ImageIcon size={13} /> Photo
                      </button>
                      <button
                        onClick={() => setView("3d")}
                        data-testid="view-360"
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider transition-colors ${view === "3d" ? "bg-[#1F6FEB] text-white" : "text-[#1F6FEB] hover:bg-[#1F6FEB]/5"}`}
                      >
                        <Rotate3d size={13} /> 360° View
                      </button>
                    </div>
                  ) : product.flagship && (
                    <span className="absolute bottom-5 left-5 inline-flex items-center gap-2 bg-white border border-[#0A1128]/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-[#1F6FEB]">
                      <Rotate3d size={14} /> 360° ready
                    </span>
                  )}
                </div>

                <div className="p-8 sm:p-10 relative">
                  <button onClick={onClose} className="absolute top-6 right-6 hidden md:block p-1 hover:text-[#1F6FEB]" aria-label="Close" data-testid="product-detail-close">
                    <X size={24} className="text-[#0A1128]" />
                  </button>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#1F6FEB]">{product.categoryName}</p>
                  <h3 className="mt-3 text-3xl font-extrabold tracking-tight text-[#0A1128] leading-tight">{product.name}</h3>
                  {product.model && <p className="mt-2 text-sm font-bold tracking-wide text-[#64748B]">{product.model}</p>}
                  <p className="mt-3 text-[#475569]">{product.spec}</p>

                  <dl className="mt-8 space-y-4 border-t border-[#0A1128]/10 pt-6">
                    {product.material && <Row k="Material" v={product.material} />}
                    {product.powder && <Row k="Powder" v={product.powder} />}
                    {product.application && <Row k="Application" v={product.application} />}
                    {product.variants && <Row k="Variants" v={product.variants.join(" · ")} />}
                  </dl>

                  <button
                    onClick={() => { onClose(); openQuote(product.name); }}
                    data-testid="product-detail-quote"
                    className="mt-9 w-full bg-[#1F6FEB] text-white hover:bg-[#1556B8] transition-colors px-8 py-4 font-bold text-sm tracking-widest uppercase"
                  >
                    Request a Quote
                  </button>
                  <p className="mt-3 text-xs text-[#94A3B8]">Specifications shown are those confirmed by Clinixport. Ranges expand on request.</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

const Row = ({ k, v }) => (
  <div className="grid grid-cols-3 gap-4">
    <dt className="text-xs font-bold uppercase tracking-wider text-[#64748B]">{k}</dt>
    <dd className="col-span-2 text-[15px] text-[#0A1128]">{v}</dd>
  </div>
);
